import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Heartbeat, Trophy, Bandaids, Trash, Shield, Warning, Info, FilePdf } from "@phosphor-icons/react";
import { WeighInPdfModal } from "./MatchValidator";

export default function AthleteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [health, setHealth] = useState([]);
  const [perf, setPerf] = useState([]);
  const [tab, setTab] = useState("health");
  const [eligibility, setEligibility] = useState(null);
  const [hForm, setHForm] = useState({ entry_type: "blessure", title: "", description: "", severity: "legere", status: "actif", date: "" });
  const [pForm, setPForm] = useState({ discipline: "", result: "", event_name: "", date: "" });

  const loadAll = () => Promise.all([
    api.get(`/athletes/${id}`).then(r => setAthlete(r.data)),
    api.get(`/athletes/${id}/health`).then(r => setHealth(r.data)),
    api.get(`/athletes/${id}/performance`).then(r => setPerf(r.data)),
    api.get(`/athletes/${id}/eligibility`).then(r => setEligibility(r.data)).catch(() => {}),
  ]).catch(() => {});
  useEffect(() => { loadAll(); }, [id]);

  const addHealth = async (e) => {
    e.preventDefault();
    try {
      const payload = {...hForm};
      if (!payload.date) delete payload.date;
      await api.post(`/athletes/${id}/health`, payload);
      setHForm({ entry_type: "blessure", title: "", description: "", severity: "legere", status: "actif", date: "" });
      loadAll();
      toast.success("Entrée ajoutée");
    } catch { toast.error("Erreur"); }
  };

  const addPerf = async (e) => {
    e.preventDefault();
    try {
      const payload = {...pForm};
      if (!payload.date) delete payload.date;
      await api.post(`/athletes/${id}/performance`, payload);
      setPForm({ discipline: "", result: "", event_name: "", date: "" });
      loadAll();
      toast.success("Performance enregistrée");
    } catch { toast.error("Erreur"); }
  };

  const deleteAth = async () => {
    if (!confirm("Supprimer cet athlète ?")) return;
    await api.delete(`/athletes/${id}`);
    toast.success("Supprimé");
    nav("/app/athletes");
  };

  if (!athlete) return <div className="p-12 text-sm text-muted-foreground">Chargement…</div>;

  return (
    <div data-testid="athlete-detail">
      <header className="hard-border-b px-8 lg:px-12 py-6">
        <Link to="/app/pointage?tab=athletes" className="ticker text-muted-foreground hover:text-foreground flex items-center gap-2 mb-4" data-testid="back-to-athletes"><ArrowLeft size={12}/> Retour</Link>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-5">
            {athlete.photo_url ? (
              <img src={athlete.photo_url} alt={athlete.full_name} className="w-20 h-20 object-cover hard-border"/>
            ) : <div className="w-20 h-20 bg-secondary hard-border"/>}
            <div>
              <div className="ticker text-muted-foreground mb-1">{athlete.sport} · {athlete.level}</div>
              <h1 className="font-display font-black text-4xl tracking-tight">{athlete.full_name}</h1>
              <div className="text-sm text-muted-foreground mt-1">{athlete.club || ""} {athlete.email && `· ${athlete.email}`} {athlete.phone && `· ${athlete.phone}`}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={async () => {
                try {
                  const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
                  const token = localStorage.getItem("map_token");
                  const resp = await fetch(`${API}/athletes/${athlete.id}/dossier.pdf`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (!resp.ok) throw new Error("Échec génération PDF");
                  const blob = await resp.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `dossier_${athlete.full_name.replace(/\s/g, "-")}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Dossier PDF téléchargé");
                } catch (e) {
                  toast.error(e.message || "Échec");
                }
              }}
              data-testid="download-dossier-pdf"
              className="hard-border px-4 py-2 ticker hover:bg-foreground hover:text-background transition-colors flex items-center gap-2"
            >
              <FilePdf size={14}/> Dossier PDF
            </button>
            <button onClick={deleteAth} data-testid="delete-athlete" className="hard-border px-4 py-2 ticker hover:bg-[#D90429] hover:text-white hover:border-[#D90429] transition-colors">Supprimer</button>
          </div>
        </div>
        {athlete.notes && <div className="mt-4 text-sm bg-secondary/40 p-4 hard-border">{athlete.notes}</div>}
      </header>

      <div className="px-8 lg:px-12 py-8">
        <div className="flex gap-0 hard-border max-w-2xl mb-6">
          <button data-testid="tab-health" onClick={()=>setTab("health")} className={`flex-1 py-3 ticker flex items-center justify-center gap-2 transition-colors ${tab==="health"?"bg-foreground text-white":"hover:bg-secondary"}`}><Heartbeat size={14}/> Carnet santé</button>
          <button data-testid="tab-perf" onClick={()=>setTab("perf")} className={`flex-1 py-3 ticker flex items-center justify-center gap-2 transition-colors ${tab==="perf"?"bg-foreground text-white":"hover:bg-secondary"}`}><Trophy size={14}/> Performances</button>
          <button data-testid="tab-eligibility" onClick={()=>setTab("eligibility")} className={`flex-1 py-3 ticker flex items-center justify-center gap-2 transition-colors ${tab==="eligibility"?"bg-foreground text-white":"hover:bg-secondary"}`}><Shield size={14}/> Éligibilité MTO</button>
        </div>

        {tab === "health" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <form onSubmit={addHealth} className="space-y-3 hard-border p-5" data-testid="health-form">
              <div className="ticker text-muted-foreground">Nouvelle entrée santé</div>
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="h-type" value={hForm.entry_type} onChange={(e)=>setHForm({...hForm, entry_type:e.target.value})} className="hard-border px-3 py-2 bg-white">
                  <option value="blessure">Blessure</option><option value="consultation">Consultation</option>
                  <option value="suivi">Suivi</option><option value="test_physique">Test physique</option>
                </select>
                <select data-testid="h-status" value={hForm.status} onChange={(e)=>setHForm({...hForm, status:e.target.value})} className="hard-border px-3 py-2 bg-white">
                  <option value="actif">Actif</option><option value="recuperation">Récupération</option><option value="resolu">Résolu</option>
                </select>
              </div>
              <input data-testid="h-title" required placeholder="Titre (ex: Entorse cheville droite)" value={hForm.title} onChange={(e)=>setHForm({...hForm, title:e.target.value})} className="w-full hard-border px-3 py-2"/>
              <textarea data-testid="h-desc" rows={3} placeholder="Description" value={hForm.description} onChange={(e)=>setHForm({...hForm, description:e.target.value})} className="w-full hard-border px-3 py-2"/>
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="h-sev" value={hForm.severity} onChange={(e)=>setHForm({...hForm, severity:e.target.value})} className="hard-border px-3 py-2 bg-white">
                  <option value="legere">Légère</option><option value="moderee">Modérée</option><option value="severe">Sévère</option>
                </select>
                <input data-testid="h-date" type="date" value={hForm.date} onChange={(e)=>setHForm({...hForm, date:e.target.value})} className="hard-border px-3 py-2"/>
              </div>
              <button className="w-full bg-[#D90429] hover:bg-[#BF0424] text-white py-2.5 ticker flex items-center justify-center gap-2 transition-colors" data-testid="h-submit"><Plus size={14}/> Ajouter</button>
            </form>
            <div className="space-y-3 max-h-[550px] overflow-y-auto" data-testid="health-list">
              {health.length === 0 && <div className="text-sm text-muted-foreground">Aucune entrée santé.</div>}
              {health.map((h) => (
                <div key={h.id} className="hard-border p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bandaids size={16} className="text-[#D90429]"/>
                      <span className="font-semibold text-sm">{h.title}</span>
                    </div>
                    <span className={`ticker px-2 py-0.5 ${h.status==="resolu"?"bg-[#2A9D8F] text-white":h.status==="recuperation"?"bg-[#E9C46A]":"bg-[#D90429] text-white"}`}>{h.status}</span>
                  </div>
                  <div className="ticker text-muted-foreground mt-1">{h.entry_type} · {h.severity || "—"} · {new Date(h.date).toLocaleDateString("fr-CA")}</div>
                  {h.description && <div className="text-sm mt-2 text-muted-foreground">{h.description}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "perf" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <form onSubmit={addPerf} className="space-y-3 hard-border p-5" data-testid="perf-form">
              <div className="ticker text-muted-foreground">Nouvelle performance</div>
              <input data-testid="p-disc" required placeholder="Discipline (ex: 100m, saut en hauteur)" value={pForm.discipline} onChange={(e)=>setPForm({...pForm, discipline:e.target.value})} className="w-full hard-border px-3 py-2"/>
              <input data-testid="p-result" required placeholder="Résultat (ex: 10,82s ou 1,92m)" value={pForm.result} onChange={(e)=>setPForm({...pForm, result:e.target.value})} className="w-full hard-border px-3 py-2 font-mono"/>
              <input data-testid="p-event" placeholder="Événement (optionnel)" value={pForm.event_name} onChange={(e)=>setPForm({...pForm, event_name:e.target.value})} className="w-full hard-border px-3 py-2"/>
              <input data-testid="p-date" type="date" value={pForm.date} onChange={(e)=>setPForm({...pForm, date:e.target.value})} className="w-full hard-border px-3 py-2"/>
              <button className="w-full bg-[#D90429] hover:bg-[#BF0424] text-white py-2.5 ticker flex items-center justify-center gap-2 transition-colors" data-testid="p-submit"><Plus size={14}/> Enregistrer</button>
            </form>
            <div className="space-y-3 max-h-[550px] overflow-y-auto" data-testid="perf-list">
              {perf.length === 0 && <div className="text-sm text-muted-foreground">Aucune performance.</div>}
              {perf.map((p) => (
                <div key={p.id} className="hard-border p-4 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-sm">{p.discipline}</div>
                    <div className="ticker text-muted-foreground">{p.event_name || "—"} · {new Date(p.date).toLocaleDateString("fr-CA")}</div>
                  </div>
                  <div className="font-mono text-lg font-semibold">{p.result}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab === "eligibility" && <EligibilityPanel athlete={athlete} eligibility={eligibility}/>}
      </div>
    </div>
  );
}

const TECH_LABEL = (v) => {
  if (v === true) return "Permis";
  if (v === false) return "Interdit";
  if (v === "limited_no_12_6") return "Limité (pas 12-6)";
  return String(v);
};
const TECH_COLOR = (v) => v === true ? "#2A9D8F" : (v === false ? "#D90429" : "#E9C46A");

function EligibilityPanel({ athlete, eligibility }) {
  const [pdfModal, setPdfModal] = useState(false);
  if (!eligibility) {
    return <div className="hard-border p-12 text-center text-sm text-muted-foreground">Calcul de l'éligibilité…</div>;
  }
  const e = eligibility;
  const cat = e.age_category;
  const cls = e.suggested_class;
  const tech = e.technique_rules;
  const eq = e.equipment;
  const rounds = e.rounds_format;
  const hasAlerts = (e.alerts || []).length > 0;
  const hasWarnings = (e.warnings || []).length > 0;

  return (
    <div className="space-y-6" data-testid="eligibility-panel">
      {/* Action bar */}
      <div className="flex items-center justify-end">
        <button onClick={() => setPdfModal(true)} data-testid="athlete-pdf-btn"
          className="bg-[#D90429] hover:bg-[#BF0424] text-white px-5 py-2.5 ticker flex items-center gap-2 transition-colors">
          <FilePdf size={16}/> Générer fiche de pesée
        </button>
      </div>
      {pdfModal && (
        <WeighInPdfModal
          athletes={[{ id: athlete.id, name: athlete.full_name }]}
          common_rules={null}
          onClose={() => setPdfModal(false)}
        />
      )}
      {/* Alertes en tête */}
      {hasAlerts && (
        <div className="space-y-2" data-testid="eligibility-alerts">
          {e.alerts.map((a, i) => (
            <div key={i} className="hard-border p-4 bg-[#D90429] text-white flex items-start gap-3">
              <Warning size={20} weight="fill" className="shrink-0 mt-0.5"/>
              <div>
                <div className="ticker">ALERTE · {a.level.toUpperCase()}</div>
                <div className="font-semibold mt-1">{a.message}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Synthèse */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 hard-border" data-testid="eligibility-summary">
        <div className="p-5 border-r border-border">
          <div className="ticker text-muted-foreground">Âge calculé</div>
          <div className="font-display font-black text-3xl mt-2">{e.computed_age ?? "—"}</div>
          <div className="ticker text-muted-foreground mt-1">{cat ? cat.label : "Date de naissance manquante"}</div>
        </div>
        <div className="p-5 border-r border-border">
          <div className="ticker text-muted-foreground">Classe suggérée</div>
          <div className="font-display font-black text-3xl mt-2 text-[#D90429]">{cls ? cls.label : "—"}</div>
          <div className="ticker text-muted-foreground mt-1">{athlete.nb_combats_total ?? 0} combats full contact</div>
        </div>
        <div className="p-5">
          <div className="ticker text-muted-foreground">Format de combat</div>
          <div className="font-display font-black text-3xl mt-2">{rounds ? `${rounds.rounds} × ${rounds.minutes} min` : "—"}</div>
          <div className="ticker text-muted-foreground mt-1">Tournoi sanctionné</div>
        </div>
      </div>

      {/* Techniques */}
      {tech && (
        <div className="hard-border" data-testid="eligibility-techniques">
          <div className="px-5 py-3 bg-foreground text-white ticker">01 · Techniques permises</div>
          <div className="p-5 space-y-3">
            <div className="text-sm text-muted-foreground italic">{tech.description}</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <TechBadge label="Frappes à la tête" value={tech.head_punches_kicks}/>
              <TechBadge label="Genoux à la tête" value={tech.knees_to_head}/>
              <TechBadge label="Coudes à la tête" value={tech.elbows_to_head}/>
            </div>
          </div>
        </div>
      )}

      {/* Équipement */}
      {eq && (
        <div className="hard-border" data-testid="eligibility-equipment">
          <div className="px-5 py-3 bg-foreground text-white ticker">02 · Équipement obligatoire (tournoi sanctionné)</div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <EqRow label="Gants" value={`${eq.gloves} · ${eq.gloves_size}`}/>
            <EqRow label="Casque" value={eq.headgear}/>
            <EqRow label="Protège-tibias" value={eq.shin_guards}/>
            <EqRow label="Coudières" value={eq.elbow_pads}/>
            <EqRow label="Protège-dents" value={eq.mouthguard}/>
            <EqRow label="Coquille" value={eq.groin_protector}/>
            <EqRow label="Plastron" value={eq.body_protector}/>
            {eq.female_chest_protection && <EqRow label="Protection poitrine fém." value={eq.female_chest_protection}/>}
            <EqRow label="Protection cheville" value={eq.ankle_support} negative/>
            <EqRow label="Souliers / chaussettes" value={eq.shoes_socks} negative/>
          </div>
        </div>
      )}

      {/* Avertissements */}
      {hasWarnings && (
        <div className="hard-border" data-testid="eligibility-warnings">
          <div className="px-5 py-3 bg-[#E9C46A]/30 ticker flex items-center gap-2"><Info size={14}/> Notes & recommandations</div>
          <div className="p-5 space-y-2 text-sm">
            {e.warnings.map((w, i) => <div key={i} className="text-muted-foreground">— {w.message}</div>)}
          </div>
        </div>
      )}

      {/* Liens vers règlements complets */}
      <div className="text-sm text-muted-foreground">
        Consulte le <Link to="/app/reglements" className="text-[#D90429] underline">tableau maître MTO/CMTO complet</Link> pour les fautes globales et les exigences médicales.
      </div>
    </div>
  );
}

function TechBadge({ label, value }) {
  return (
    <div className="hard-border p-4">
      <div className="ticker text-muted-foreground">{label}</div>
      <div className="font-semibold mt-1" style={{ color: TECH_COLOR(value) }}>{TECH_LABEL(value)}</div>
    </div>
  );
}

function EqRow({ label, value, negative }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1 border-b border-border last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-medium text-right ${negative ? 'text-[#D90429]' : ''}`}>{value}</span>
    </div>
  );
}
